import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// Environment variable validation (copied from shared client)
const metaEnv =
  typeof import.meta !== "undefined" ? (import.meta as any).env : undefined;
const supabaseUrl =
  (metaEnv && metaEnv.VITE_SUPABASE_URL) ||
  (typeof process !== "undefined" ? process.env?.VITE_SUPABASE_URL : undefined);
const supabaseAnonKey =
  (metaEnv && metaEnv.VITE_SUPABASE_ANON_KEY) ||
  (typeof process !== "undefined"
    ? process.env?.VITE_SUPABASE_ANON_KEY
    : undefined);

export const useKioskVertretungsplanData = (currentDate: Date) => {
  const [data, setData] = useState<any>({
    lessons: [],
    classAbsences: [],
    personalAbsences: [],
    availableTeachers: [],
    rooms: [],
    periods: [],
    aufsichtSubstitutions: [],
    metadata: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to cancel previous requests
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    const jwt = localStorage.getItem("fw_kiosk_jwt");
    if (!jwt) {
      setError("Kiosk session invalid (missing JWT)");
      setLoading(false);
      return;
    }

    if (!supabaseUrl || !supabaseAnonKey) {
      setError("Supabase configuration missing");
      setLoading(false);
      return;
    }

    // Create a client specifically for this request with the Kiosk JWT
    const kioskClient = createClient(supabaseUrl, supabaseAnonKey, {
      global: {
        headers: {
          Authorization: `Bearer ${jwt}`,
        },
      },
    });

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);

    try {
      // Fix: Use local date string to match database dates correctly
      // toISOString() converts to UTC, which might be yesterday late at night
      const offset = currentDate.getTimezoneOffset();
      const localDate = new Date(currentDate.getTime() - offset * 60 * 1000);
      const dateStr = localDate.toISOString().split("T")[0];
      console.log("🔍 [KIOSK DEBUG] Fetching for Date:", dateStr);

      // Parallel fetch similar to useVertretungsplanerData
      // Note: We assume the Kiosk role has RLS access to these views/RPCs.
      const [
        lessonsRes,
        teachersRes,
        roomsRes,
        periodsRes,
        schoolInfoRes,
        absencesRes,
        classAbsencesRes,
        aufsichtRes,
      ] = await Promise.allSettled([
        kioskClient
          .from("vw_kiosk_published_substitutions")
          .select(
            "lesson_id, period_id, period_label, period_start_time, period_end_time, class_name, subject_name, course_name, room_number, original_primary_teacher_id, original_teacher_ids, substitute_primary_teacher_id, substitute_begleitung_teacher_ids, substitute_comment, substitute_room_id, substitute_published, substitute_is_cancelled, substitution_type, is_primary_teacher_absent, substitution_reason",
          )
          .eq("lesson_date", dateStr),
        kioskClient
          .from("vw_kiosk_staff_names")
          .select("id, first_name, last_name, kurzel, role_name"), // Use secure view
        kioskClient.from("structure_rooms").select("id, name, room_number"),
        kioskClient
          .from("schedule_periods")
          .select("id, label, start_time, end_time")
          .order("start_time"),
        kioskClient
          .from("vw_react_school_information")
          .select("name, logo_url")
          .limit(1)
          .maybeSingle(),
        kioskClient
          .from("vw_kiosk_daily_absences")
          .select(
            "id, staff_id, display_name, status_text, absence_type, partial_start_time, partial_end_time",
          )
          .eq("date", dateStr),
        kioskClient
          .from("vw_kiosk_class_absences")
          .select("*")
          .lte("start_date", dateStr)
          .gte("end_date", dateStr),
        kioskClient
          .from("vw_kiosk_aufsicht_substitutions")
          .select("*")
          .eq("date", dateStr),
      ]);

      const newData: any = {
        lessons: [],
        classAbsences: [],
        personalAbsences: [],
        availableTeachers: [],
        rooms: [],
        periods: [],
        aufsichtSubstitutions: [],
        metadata: {},
      };

      // Populate class absences
      if (
        classAbsencesRes.status === "fulfilled" &&
        !classAbsencesRes.value.error
      ) {
        newData.classAbsences = classAbsencesRes.value.data || [];
      } else {
        newData.classAbsences = [];
      }

      newData.personalAbsences = [];

      // Populate Aufsicht substitutions
      if (aufsichtRes.status === "fulfilled" && !aufsichtRes.value.error) {
        newData.aufsichtSubstitutions = aufsichtRes.value.data || [];
      } else {
        newData.aufsichtSubstitutions = [];
      }

      // Process Teachers (needed for mapping IDs to names/kurzels)
      let teacherMap = new Map<string, any>();
      if (teachersRes.status === "fulfilled" && !teachersRes.value.error) {
        newData.availableTeachers = teachersRes.value.data || [];
        (newData.availableTeachers || []).forEach((t: any) => {
          if (t.id) teacherMap.set(t.id, t);
        });
      }

      // Process Personal Absences
      if (absencesRes.status === "fulfilled" && !absencesRes.value.error) {
        const absencesData = absencesRes.value.data || [];

        newData.personalAbsences = absencesData.map((a: any) => {
          return {
            id: a.id,
            staff_id: a.staff_id,
            name: a.display_name || "Unknown", // Use backend-provided display name
            status: a.status_text, // Use backend-provided status text
            type: a.absence_type,
            startTime: a.partial_start_time, // Partial day absence start time
            endTime: a.partial_end_time, // Partial day absence end time
          };
        });
      } else {
        console.warn(
          "Failed to fetch absences:",
          (absencesRes as any).reason || (absencesRes as any).value?.error,
        );
      }

      // Create a Map of absent staff IDs with their absence time ranges
      // Key: staff_id, Value: array of {startTime, endTime} for each absence
      const absentStaffTimeRanges = new Map<
        string,
        Array<{ startTime: string | null; endTime: string | null }>
      >();
      newData.personalAbsences.forEach((a: any) => {
        if (a.staff_id) {
          const ranges = absentStaffTimeRanges.get(a.staff_id) || [];
          ranges.push({
            startTime: a.startTime || null,
            endTime: a.endTime || null,
          });
          absentStaffTimeRanges.set(a.staff_id, ranges);
        }
      });

      // Helper function to check if a staff member is absent during a specific lesson period
      // Uses proper time-range OVERLAP check: lesson overlaps absence if
      // absenceStart <= periodEnd AND periodStart <= absenceEnd
      const isStaffAbsentForPeriod = (
        staffId: string,
        periodStartTime: string | null,
        periodEndTime: string | null,
      ): boolean => {
        const ranges = absentStaffTimeRanges.get(staffId);
        if (!ranges) return false;

        // If we don't have the period start time, assume absent if any absence exists
        if (!periodStartTime) return true;

        // Extract just HH:MM from period times for comparison
        const periodStart = periodStartTime.substring(0, 5);
        const periodEnd = periodEndTime?.substring(0, 5) || periodStart;

        // Check each absence range
        for (const range of ranges) {
          // If no time range specified (full-day absence), staff is absent
          if (!range.startTime && !range.endTime) return true;

          const absStart = range.startTime?.substring(0, 5) || "00:00";
          const absEnd = range.endTime?.substring(0, 5) || "23:59";

          // Proper time-range overlap: two ranges overlap if each starts before or at the other's end
          if (absStart <= periodEnd && periodStart <= absEnd) {
            return true;
          }
        }
        return false;
      };

      // Process Rooms
      let roomMap = new Map<string, string>();
      if (roomsRes.status === "fulfilled" && !roomsRes.value.error) {
        newData.rooms = roomsRes.value.data || [];
        (newData.rooms || []).forEach((r: any) => {
          if (r.id) roomMap.set(r.id, r.room_number || r.name);
        });
      }

      // Process Periods
      if (periodsRes.status === "fulfilled" && !periodsRes.value.error) {
        newData.periods = periodsRes.value.data || [];
      }

      // Process Lessons
      if (lessonsRes.status === "fulfilled" && !lessonsRes.value.error) {
        // Transform lessons to match expected format
        newData.lessons = (lessonsRes.value.data || []).map((l: any) => {
          // Resolve teacher names (prefer last_name, fallback to kurzel)
          const getTeacherName = (id: string) => {
            const t = teacherMap.get(id);
            return t?.last_name || t?.kurzel || "";
          };

          const originalTeacherName = getTeacherName(
            l.original_primary_teacher_id,
          );
          const substitutePrimaryName = getTeacherName(
            l.substitute_primary_teacher_id,
          );

          const substituteBegleitungNames: string[] = [];
          if (Array.isArray(l.substitute_begleitung_teacher_ids)) {
            l.substitute_begleitung_teacher_ids.forEach((id: string) => {
              const n = getTeacherName(id);
              if (n) substituteBegleitungNames.push(n);
            });
          }

          const substituteRoomName = roomMap.get(l.substitute_room_id) || null;

          // Calculate absence status before returning object
          // Check if primary teacher is absent during THIS specific lesson's period
          const isPrimaryAbsent =
            l.is_primary_teacher_absent ||
            isStaffAbsentForPeriod(
              l.original_primary_teacher_id,
              l.period_start_time,
              l.period_end_time,
            );

          const allTeacherIds = Array.isArray(l.original_teacher_ids)
            ? [
                ...new Set(
                  [
                    ...l.original_teacher_ids,
                    l.original_primary_teacher_id,
                  ].filter(Boolean),
                ),
              ]
            : [l.original_primary_teacher_id].filter(Boolean);

          return {
            id: l.lesson_id,
            period: l.period_id,
            periodStart: l.period_start_time,
            class: l.class_name,
            subject: l.subject_name,
            course_name: l.course_name || null,
            room: l.room_number,

            original_primary_teacher_id: l.original_primary_teacher_id,
            original_teacher_kurzel: originalTeacherName,
            teacher_ids: allTeacherIds,

            // Check if teacher is actually absent (not a self-substitution or room change)
            absenceType: isPrimaryAbsent ? "primary" : "none",
            primary_teacher_absent: isPrimaryAbsent,
            absent_teacher_ids: allTeacherIds.filter(
              (id: string) =>
                isStaffAbsentForPeriod(
                  id,
                  l.period_start_time,
                  l.period_end_time,
                ) ||
                (id === l.original_primary_teacher_id && isPrimaryAbsent),
            ),

            substitute_primary_teacher_id: l.substitute_primary_teacher_id,
            substitute_primary_teacher_kurzel: substitutePrimaryName,

            substitute_begleitung_teacher_ids:
              l.substitute_begleitung_teacher_ids,
            substitute_begleitung_teacher_kurzels: substituteBegleitungNames,

            substitute_comment: l.substitute_comment,
            substitute_room: substituteRoomName, // Layout expects name here
            substitute_published: l.substitute_published,
            substitute_is_cancelled: l.substitute_is_cancelled,
            substitution_reason: l.substitution_reason,
            substitution_type: l.substitution_type,
          };
        });
      } else {
        console.error(
          "Error fetching lessons",
          JSON.stringify(
            (lessonsRes as any).value?.error || lessonsRes,
            null,
            2,
          ),
        );
      }

      console.log("🔍 [KIOSK DEBUG] Raw Lessons Response:", lessonsRes);
      console.log("🔍 [KIOSK DEBUG] Processed Lessons:", newData.lessons);

      // Process School Info
      if (schoolInfoRes.status === "fulfilled") {
        // It's okay if this is null or error, we just won't show the logo
        const info = !schoolInfoRes.value.error
          ? schoolInfoRes.value.data
          : null;
        let logoUrl = null;

        // Convert logo path to public URL if it's a storage path
        // (Kiosk role can't create signed URLs, so use public URL directly)
        if (info?.logo_url && !info.logo_url.startsWith("http")) {
          try {
            const {
              data: { publicUrl },
            } = kioskClient.storage
              .from("school-logo")
              .getPublicUrl(info.logo_url);
            logoUrl = publicUrl;
          } catch (logoErr) {
            console.warn("Could not get public URL for logo:", logoErr);
          }
        } else if (info?.logo_url) {
          logoUrl = info.logo_url;
        }

        newData.metadata = {
          school_name: info?.name || "Vertretungsplan",
          school_logo_url: logoUrl,
        };
      } else {
        newData.metadata = { school_name: "Vertretungsplan" };
      }

      setData(newData);
    } catch (err: any) {
      console.error("Kiosk fetch error:", err);
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};
